// frontend/src/components/daytrader/SessionClock.tsx
//
// Where are we in the 24h crypto day? Current session (Asia / London / NY /
// Late) on a UTC bar with a now-marker, plus countdowns to the next session
// change and the next funding. Ticks every second client-side; the clock is
// re-synced to the server (/api/intraday/session) every few minutes so a
// drifting local clock can't mislabel the session.

import { useEffect, useState } from "react";
import { Globe2 } from "lucide-react";
import { Tooltip } from "@heroui/react";
import { cn } from "@/lib/utils";
import {
  FUNDING_HOURS, SESSION_HOURS, SESSION_META, SESSION_ORDER,
  dayFraction, formatCountdown, nextFundingAt, nextSessionChangeAt, sessionOf, utcHm,
  type SessionResponse,
} from "./dayTrader";

const RESYNC_MS = 5 * 60_000;

/** Server-minus-local offset in seconds, refreshed every RESYNC_MS. */
function useServerOffset(): { offset: number; synced: boolean } {
  const [state, setState] = useState({ offset: 0, synced: false });
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      try {
        const t0 = Date.now();
        const res = await fetch("/api/intraday/session");
        const json = (await res.json()) as SessionResponse;
        if (cancelled || !json.success) return;
        const localMid = (t0 + Date.now()) / 2000;
        setState({ offset: json.atSec - localMid, synced: true });
      } catch { /* keep local clock */ }
    };
    sync();
    const id = setInterval(sync, RESYNC_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return state;
}

function useNowSec(offset: number): number {
  const [now, setNow] = useState(() => Date.now() / 1000 + offset);
  useEffect(() => {
    const tick = () => setNow(Date.now() / 1000 + offset);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [offset]);
  return now;
}

/**
 * Compact 24h session bar for the desk. The app TopBar already shows the big
 * session / funding countdowns, so this is one dense row: current session
 * chip, the UTC day as coloured session blocks with funding ticks and a
 * now-marker, and small next-session / funding countdowns.
 */
export function SessionClock({ className = "" }: { className?: string }) {
  const { offset, synced } = useServerOffset();
  const now = Math.floor(useNowSec(offset));
  const session = sessionOf(now);
  const change = nextSessionChangeAt(now);
  const funding = nextFundingAt(now);
  const meta = SESSION_META[session];
  const fundingSoon = funding - now <= 15 * 60;

  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-border bg-surface px-3 py-2", className)}>
      <Tooltip delay={200}>
        <Tooltip.Trigger aria-label="Trading sessions in UTC">
          <span className="flex items-center gap-1.5">
            <Globe2 className="size-3.5 text-muted" aria-hidden />
            <span className={cn("text-xs font-semibold", meta.text)}>{meta.label}</span>
            <span className="num text-2xs text-muted">{utcHm(now)}{!synced && " local"}</span>
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content>Sessions in UTC: Asia 00–07, London 07–13, New York 13–20, Late 20–24</Tooltip.Content>
      </Tooltip>

      <DayBar nowSec={now} className="order-last min-w-0 basis-full sm:order-none sm:basis-auto sm:flex-1" />

      <div className="flex items-center gap-3 text-2xs text-muted">
        <span title={`${SESSION_META[change.to].label} opens at ${utcHm(change.atSec)} UTC`}>
          {SESSION_META[change.to].short} <span className="num text-foreground">{formatCountdown(change.atSec - now)}</span>
        </span>
        <span title={`Binance perp funding settles at 00:00 / 08:00 / 16:00 UTC — next ${utcHm(funding)} UTC`}>
          Fund <span className={cn("num", fundingSoon ? "text-warning" : "text-foreground")}>{formatCountdown(funding - now)}</span>
        </span>
      </div>
    </div>
  );
}

/** 24h UTC bar: coloured session blocks, funding ticks, now marker. */
function DayBar({ nowSec, className }: { nowSec: number; className?: string }) {
  const nowPct = dayFraction(nowSec) * 100;
  const current = sessionOf(nowSec);
  return (
    <div
      className={cn("relative h-5 overflow-hidden rounded-md bg-default", className)}
      role="img"
      aria-label={`UTC day, now ${utcHm(nowSec)}, ${SESSION_META[current].label} session`}
    >
      {SESSION_ORDER.map(s => {
        const [a, b] = SESSION_HOURS[s];
        const m = SESSION_META[s];
        return (
          <div
            key={s}
            className={cn("absolute inset-y-0 flex items-center border-r border-surface pl-1.5", m.bar, s === current ? "" : "opacity-45")}
            style={{ left: `${(a / 24) * 100}%`, width: `${((b - a) / 24) * 100}%` }}
            title={`${m.label} ${String(a).padStart(2, "0")}:00–${String(b).padStart(2, "0")}:00 UTC`}
          >
            <span className={cn("text-[9px] font-semibold tracking-wider", s === current ? "text-foreground" : "text-foreground/60")}>{m.short}</span>
          </div>
        );
      })}
      {FUNDING_HOURS.filter(h => h > 0).map(h => (
        <div key={h} className="absolute top-0 h-1.5 w-px bg-foreground/50" style={{ left: `${(h / 24) * 100}%` }} title={`Funding ${String(h).padStart(2, "0")}:00 UTC`} />
      ))}
      <div className="absolute inset-y-0 w-0.5 bg-foreground" style={{ left: `calc(${nowPct}% - 1px)` }} />
    </div>
  );
}
